(function () {
  "use strict";
  if (window.__npVCLoaded) return;
  window.__npVCLoaded = true;

  class NpVideoCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector("[data-np-vc-track]");
      if (!this.track) return;
      this.prev = root.querySelector("[data-np-vc-prev]");
      this.next = root.querySelector("[data-np-vc-next]");
      this.looping = root.hasAttribute("data-np-vc-loop");

      this.realCount = 0;
      this.realStart = 0;
      this.cards = [];
      this.activeIdx = 0;
      this.currentOffset = 0;

      this.isDown = false;
      this.dragged = false;
      this.captured = false;
      this.pointerId = null;
      this.startX = 0;
      this.startOffset = 0;
      this.lastX = 0;
      this.lastT = 0;
      this.velocity = 0;
      this.momentumRaf = null;
      this.dragRaf = null;
      this.pendingDragX = 0;
      this.settleTimer = null;
      this.wheelTimer = null;
      this._smooth = true;

      this.init();
    }

    init() {
      const originals = Array.from(this.track.children);
      if (this.looping && originals.length >= 2) {
        this.realCount = originals.length;
        this.cloneCards(originals);
        this.realStart = this.realCount;
      } else {
        this.looping = false;
        this.realCount = originals.length;
        this.realStart = 0;
      }

      this.cards = Array.from(this.track.children);
      this.activeIdx = this.realStart;
      this.updateActive();
      this.bind();

      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(() => {
          if (this.root.clientWidth > 0) this.goTo(this.activeIdx, false);
        });
        this.ro.observe(this.root);
      } else {
        requestAnimationFrame(() => this.goTo(this.activeIdx, false));
      }

      this.updateButtons();
    }

    cloneCards(originals) {
      originals.forEach((c) => {
        const clone = c.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.classList.add("np-vc-clone");
        this.track.appendChild(clone);
      });
      const frag = document.createDocumentFragment();
      originals.forEach((c) => {
        const clone = c.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.classList.add("np-vc-clone");
        frag.appendChild(clone);
      });
      this.track.insertBefore(frag, this.track.firstChild);
    }

    updateActive() {
      this.cards.forEach((c, i) =>
        c.classList.toggle("is-active", i === this.activeIdx),
      );
    }

    pauseInactive() {
      this.cards.forEach((c, i) => {
        if (i === this.activeIdx) return;
        const v = c.querySelector("video");
        if (v && !v.paused) v.pause();
        if (v) v.removeAttribute("controls");
        c.classList.remove("is-playing");
      });
    }

    centerOffsetFor(idx) {
      const cs = getComputedStyle(this.root);
      const wAct =
        parseFloat(cs.getPropertyValue("--np-vc-card-w-active")) || 440;
      const wIn = parseFloat(cs.getPropertyValue("--np-vc-card-w")) || 280;
      const ts = getComputedStyle(this.track);
      const gap = parseFloat(ts.gap) || 0;
      const padL = parseFloat(ts.paddingLeft) || 0;
      const trackOffsetLeft = this.track.offsetLeft;

      const cardLeft = padL + idx * (wIn + gap);
      const cardCenterInTrack = cardLeft + wAct / 2;
      const sectionCenter = this.root.clientWidth / 2;
      return sectionCenter - trackOffsetLeft - cardCenterInTrack;
    }

    _setSmooth() {
      if (this._smooth) return;
      this.track.offsetHeight;
      this.track.style.transition = "";
      this._smooth = true;
    }

    _setInstant() {
      if (!this._smooth) return;
      this.track.style.transition = "none";
      this._smooth = false;
    }

    _translateX(x) {
      this.track.style.transform = `translateX(${x}px)`;
      this.currentOffset = x;
    }

    goTo(idx, smooth = true) {
      idx = Math.max(0, Math.min(this.cards.length - 1, idx));
      this.activeIdx = idx;
      this.updateActive();
      this.pauseInactive();

      const target = this.centerOffsetFor(idx);
      if (smooth) this._setSmooth();
      else this._setInstant();
      this._translateX(target);

      clearTimeout(this.settleTimer);
      if (smooth) {
        this.settleTimer = setTimeout(() => {
          const corrected = this.centerOffsetFor(this.activeIdx);
          if (Math.abs(corrected - this.currentOffset) > 0.5) {
            this._translateX(corrected);
          }
          this.normalizeLoop();
        }, 650);
      } else {
        this.track.offsetHeight;
        this.normalizeLoop();
      }

      this.updateButtons();
    }

    normalizeLoop() {
      if (!this.looping) return;
      let newIdx = this.activeIdx;
      if (this.activeIdx < this.realStart) {
        newIdx = this.activeIdx + this.realCount;
      } else if (this.activeIdx >= this.realStart + this.realCount) {
        newIdx = this.activeIdx - this.realCount;
      }
      if (newIdx === this.activeIdx) return;

      this.activeIdx = newIdx;
      this.updateActive();
      this._setInstant();
      this._translateX(this.centerOffsetFor(newIdx));
    }

    findClosestToCenter() {
      let closest = 0,
        best = Infinity;
      this.cards.forEach((c, i) => {
        const t = this.centerOffsetFor(i);
        const d = Math.abs(t - this.currentOffset);
        if (d < best) {
          best = d;
          closest = i;
        }
      });
      return closest;
    }

    updateButtons() {
      if (this.looping) {
        if (this.prev) this.prev.disabled = false;
        if (this.next) this.next.disabled = false;
        return;
      }
      if (this.prev) this.prev.disabled = this.activeIdx === 0;
      if (this.next)
        this.next.disabled = this.activeIdx === this.cards.length - 1;
    }

    onDown(e) {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      this.cancelMomentum();
      this.cancelDragRaf();
      this.isDown = true;
      this.dragged = false;
      this.captured = false;
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.startOffset = this.currentOffset;
      this.lastX = e.clientX;
      this.lastT = performance.now();
      this.velocity = 0;
    }

    onMove(e) {
      if (!this.isDown) return;
      const dx = e.clientX - this.startX;

      if (!this.dragged && Math.abs(dx) > 4) {
        this.dragged = true;
        this.track.classList.add("is-dragging");
        try {
          this.track.setPointerCapture(this.pointerId);
          this.captured = true;
        } catch (_) {}
        this._setInstant();
      }
      if (!this.dragged) return;

      this.pendingDragX = this.startOffset + dx;
      if (!this.dragRaf) {
        this.dragRaf = requestAnimationFrame(() => {
          this.dragRaf = null;
          this._translateX(this.pendingDragX);
        });
      }

      const now = performance.now();
      const dt = now - this.lastT;
      if (dt > 0) this.velocity = (e.clientX - this.lastX) / dt;
      this.lastX = e.clientX;
      this.lastT = now;
    }

    onUp(e) {
      if (!this.isDown) return;
      this.isDown = false;
      if (!this.dragged) return;

      this.cancelDragRaf();
      this.track.classList.remove("is-dragging");
      if (this.captured) {
        try {
          this.track.releasePointerCapture(this.pointerId);
        } catch (_) {}
      }
      this.captured = false;

      if (Math.abs(this.velocity) > 0.1) this.applyMomentum();
      else this.goTo(this.findClosestToCenter());
    }

    onClick(e) {
      if (this.dragged) {
        e.preventDefault();
        e.stopPropagation();
        setTimeout(() => {
          this.dragged = false;
        }, 0);
      }
    }

    applyMomentum() {
      const friction = 0.93;
      const minV = 0.05;

      const tick = () => {
        const dx = this.velocity * 16.67;
        this._setInstant();
        this._translateX(this.currentOffset + dx);
        this.velocity *= friction;

        if (Math.abs(this.velocity) > minV) {
          this.momentumRaf = requestAnimationFrame(tick);
        } else {
          this.momentumRaf = null;
          this.goTo(this.findClosestToCenter());
        }
      };
      this.momentumRaf = requestAnimationFrame(tick);
    }

    cancelMomentum() {
      if (this.momentumRaf) cancelAnimationFrame(this.momentumRaf);
      this.momentumRaf = null;
      clearTimeout(this.settleTimer);
      clearTimeout(this.wheelTimer);
      this.velocity = 0;
    }

    cancelDragRaf() {
      if (this.dragRaf) cancelAnimationFrame(this.dragRaf);
      this.dragRaf = null;
    }

    onWheel(e) {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      if (absX < 6 || absX < absY * 1.4) return;

      e.preventDefault();
      this.cancelMomentum();
      this._setInstant();
      this._translateX(this.currentOffset - e.deltaX);

      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => {
        this.goTo(this.findClosestToCenter());
      }, 140);
    }

    bind() {
      window.addEventListener("resize", () => {
        this.cancelMomentum();
        this.goTo(this.activeIdx, false);
      });

      this.track.addEventListener("pointerdown", (e) => this.onDown(e));
      this.track.addEventListener("pointermove", (e) => this.onMove(e));
      this.track.addEventListener("pointerup", (e) => this.onUp(e));
      this.track.addEventListener("pointercancel", (e) => this.onUp(e));
      this.track.addEventListener("click", (e) => this.onClick(e), true);
      this.track.addEventListener("dragstart", (e) => e.preventDefault());
      this.track.addEventListener("wheel", (e) => this.onWheel(e), {
        passive: false,
      });

      this.cards.forEach((card, i) => {
        const playBtn = card.querySelector("[data-np-vc-play]");
        const video = card.querySelector("video");

        card.addEventListener("click", (e) => {
          if (this.dragged) return;
          if (e.target.closest("[data-np-vc-play]")) return;
          if (e.target.tagName === "VIDEO") return;
          if (i !== this.activeIdx) this.goTo(i);
        });

        if (playBtn) {
          playBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            if (this.dragged) return;
            if (i !== this.activeIdx) {
              this.goTo(i);
              setTimeout(() => this.togglePlay(this.activeIdx), 650);
            } else {
              this.togglePlay(i);
            }
          });
        }

        if (video) {
          video.addEventListener("pause", () => {
            card.classList.remove("is-playing");
          });
          video.addEventListener("ended", () => {
            card.classList.remove("is-playing");
            video.removeAttribute("controls");
            video.currentTime = 0;
          });
          video.addEventListener("play", () => {
            card.classList.add("is-playing");
          });
        }
      });

      if (this.prev)
        this.prev.addEventListener("click", () =>
          this.goTo(this.activeIdx - 1),
        );
      if (this.next)
        this.next.addEventListener("click", () =>
          this.goTo(this.activeIdx + 1),
        );
    }

    togglePlay(idx) {
      const card = this.cards[idx];
      const video = card.querySelector("video");
      if (!video) return;

      this.cards.forEach((c, i) => {
        if (i === idx) return;
        const v = c.querySelector("video");
        if (v) {
          v.pause();
          v.removeAttribute("controls");
        }
        c.classList.remove("is-playing");
      });

      if (video.paused) {
        video.setAttribute("controls", "");
        const p = video.play();
        if (p && p.then) {
          p.then(() => card.classList.add("is-playing")).catch(() => {
            video.removeAttribute("controls");
          });
        } else {
          card.classList.add("is-playing");
        }
      } else {
        video.pause();
      }
    }
  }

  function initRoot(el) {
    if (el.__npVcInit) return;
    el.__npVcInit = true;
    new NpVideoCarousel(el);
  }
  function init(scope) {
    (scope || document).querySelectorAll("[data-np-vc]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (e) => init(e.target));
  window.initNpVideoCarousels = init;
})();
